import { Injectable } from '@angular/core';

/**
 * Serviço de armazenamento seguro que protege o sessionStorage contra adulteração.
 * Utiliza assinatura de integridade (hash) para detectar modificações externas.
 * Se um valor for editado manualmente (ex: via DevTools), a validação falha
 * e a sessão é invalidada.
 */
@Injectable({
	providedIn: 'root',
})
export class SecureStorageService {
	private readonly SIGNATURE_SUFFIX = '__sig';
	private readonly SECRET_KEY = 'S@CIM_S3CUR3_K3Y_2026';

	/**
	 * Armazena um valor no sessionStorage com assinatura de integridade.
	 * @param key Chave do item
	 * @param value Valor a ser armazenado
	 */
	setItem(key: string, value: string): void {
		const signature = this.generateSignature(key, value);
		sessionStorage.setItem(key, value);
		sessionStorage.setItem(key + this.SIGNATURE_SUFFIX, signature);
	}

	/**
	 * Recupera um valor do sessionStorage e valida a integridade.
	 * Retorna null se o valor foi adulterado ou não existe.
	 * @param key Chave do item
	 */
	getItem(key: string): string | null {
		const value = sessionStorage.getItem(key);
		if (value === null) {
			return null;
		}

		const storedSignature = sessionStorage.getItem(key + this.SIGNATURE_SUFFIX);
		if (!storedSignature) {
			// Valor existe mas sem assinatura - possível adulteração
			this.handleTampering(key);
			return null;
		}

		const expectedSignature = this.generateSignature(key, value);
		if (storedSignature !== expectedSignature) {
			// Assinatura não corresponde - valor foi adulterado
			this.handleTampering(key);
			return null;
		}

		return value;
	}

	/**
	 * Remove um item e sua assinatura do sessionStorage.
	 * @param key Chave do item
	 */
	removeItem(key: string): void {
		sessionStorage.removeItem(key);
		sessionStorage.removeItem(key + this.SIGNATURE_SUFFIX);
	}

	/**
	 * Limpa todo o sessionStorage (incluindo assinaturas).
	 */
	clear(): void {
		sessionStorage.clear();
	}

	/**
	 * Verifica a integridade de todos os itens protegidos.
	 * Retorna true se todos os itens estão íntegros.
	 */
	verifyAllIntegrity(): boolean {
		const keys = this.getProtectedKeys();
		for (const key of keys) {
			const value = sessionStorage.getItem(key);
			const storedSignature = sessionStorage.getItem(key + this.SIGNATURE_SUFFIX);
			if (value !== null && storedSignature) {
				const expectedSignature = this.generateSignature(key, value);
				if (storedSignature !== expectedSignature) {
					return false;
				}
			}
		}
		return true;
	}

	/**
	 * Retorna as chaves protegidas (que possuem assinatura).
	 */
	private getProtectedKeys(): string[] {
		const keys: string[] = [];
		for (let i = 0; i < sessionStorage.length; i++) {
			const key = sessionStorage.key(i);
			if (key && !key.endsWith(this.SIGNATURE_SUFFIX)) {
				const sigKey = key + this.SIGNATURE_SUFFIX;
				if (sessionStorage.getItem(sigKey) !== null) {
					keys.push(key);
				}
			}
		}
		return keys;
	}

	/**
	 * Gera uma assinatura de integridade para o par chave-valor.
	 * Usa um hash simples baseado em operações de string para evitar
	 * dependências externas de criptografia.
	 */
	private generateSignature(key: string, value: string): string {
		const data = `${this.SECRET_KEY}:${key}:${value}`;
		return this.hashString(data);
	}

	/**
	 * Implementação de hash (FNV-1a variante) para gerar assinaturas.
	 * Suficiente para detectar adulteração manual.
	 */
	private hashString(str: string): string {
		let hash1 = 0x811c9dc5;
		let hash2 = 0x01000193;

		for (let i = 0; i < str.length; i++) {
			const char = str.charCodeAt(i);
			hash1 ^= char;
			hash1 = Math.imul(hash1, 0x01000193);
			hash2 ^= char;
			hash2 = Math.imul(hash2, 0x811c9dc5);
		}

		const hex1 = (hash1 >>> 0).toString(16).padStart(8, '0');
		const hex2 = (hash2 >>> 0).toString(16).padStart(8, '0');
		return hex1 + hex2;
	}

	/**
	 * Lida com a detecção de adulteração: remove o item comprometido
	 * e registra um aviso no console.
	 */
	private handleTampering(key: string): void {
		console.error(
			`[SecureStorage] Adulteração detectada na chave "${key}". Valor removido.`
		);
		this.removeItem(key);
	}
}
